import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'
import { media } from '../../config/settings/media'

export const TrackingCode = styled.strong`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.95em;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  width: 100%;
`

export const TextArea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 120px;
  resize: vertical;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 8px;
  padding: 0.75rem 12px;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.background};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.action.primary.bg};
    box-shadow: 0 0 0 3px rgba(62, 207, 142, 0.18);
  }
`

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const Field = styled.div`
  display: flex;
  flex-direction: column;
`

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const FilterChip = styled.button`
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.action.primary.bg : theme.colors.borderDefault};
  background: ${({ $active, theme }) =>
      $active
      ? theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.15)'
        : 'rgba(62, 207, 142, 0.12)'
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
`

export const TicketList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;
`

export const TicketCard = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  width: 100%;
  box-sizing: border-box;
  padding: 0.95rem 1rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) =>
    theme.name === 'dark' ? theme.colors.backgroundSecondary : theme.colors.borderSubtle};
  transform-origin: center top;
  transition: opacity 0.28s ease, transform 0.28s ease;

  ${({ $exiting }) =>
    $exiting &&
    css`
      opacity: 0;
      transform: translateY(-8px) scale(0.985);
      pointer-events: none;
    `}
`

export const TicketMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.85rem;
  align-items: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const TicketBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
`

export const TicketMessage = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: pre-wrap;
`

export const TicketActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
`

export const EmptyState = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const HelpCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1.1rem 1.2rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};

  ${media.lg`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `}
`

export const HelpCardCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export const HelpCardTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const HelpCardBody = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const HelpCardLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: transparent;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.action.primary.bg};
    color: ${({ theme }) => theme.colors.action.primary.bgActive};
  }
`
